import React, { useMemo, useState } from "react";
import { Button, Card, Input, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ActionPlanAiObjectiveListState } from "@/types/action-plan-v2/ActionPlanAiObjectiveList";
import apCard from "./apCard.module.css";
import cardStyles from "./ActionPlanObjectiveListCard.module.css";

type Props = {
  state: ActionPlanAiObjectiveListState;
  onPick: (objectiveId: string) => void | Promise<void>;
};

/** Tên mục tiêu dạng «… (6)» → tách số cuối thành badge (số việc đang gắn). */
const splitCount = (ten: string) => {
  const m = ten.match(/^(.*?)\s*\((\d+)\)\s*$/);
  return m ? { label: m[1], count: m[2] } : { label: ten, count: "" };
};

const fold = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").toLowerCase();

export const ActionPlanObjectiveListCard: React.FC<Props> = ({ state, onPick }) => {
  const { list } = state;
  const items = useMemo(() => list.items ?? [], [list.items]);
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const k = fold(q.trim());
    return k ? items.filter((i) => fold(i.ten || "").includes(k)) : items;
  }, [items, q]);

  return (
    <Card
      size="small"
      title="Chọn mục tiêu"
      className={apCard.card}
      extra={items.length ? <span className={apCard.badge}>{items.length}</span> : null}
    >
      {items.length > 6 ? (
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Tìm mục tiêu"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={apCard.toolbar}
          aria-label="Tìm mục tiêu"
        />
      ) : null}
      {shown.length === 0 ? (
        <div className={cardStyles.meta}>
          {items.length ? "Không có mục tiêu khớp từ khoá." : "Không có mục tiêu phù hợp trong phạm vi của bạn."}
        </div>
      ) : (
        <div className={cardStyles.list}>
          {shown.map((item) => {
            const { label, count } = splitCount(item.ten || item.id);
            return (
              <div key={item.id} className={cardStyles.row}>
                <span className={cardStyles.title}>{label}</span>
                {count ? (
                  <Tooltip title="Số việc đang gắn mục tiêu">
                    <span className={apCard.badge}>{count}</span>
                  </Tooltip>
                ) : null}
                <Button size="small" type="primary" ghost onClick={() => void onPick(item.id)}>
                  Chọn
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
